import React from 'react';
import { PiCurrencyBtcFill } from 'react-icons/pi';
import { AiOutlineEuro, AiOutlineDollarCircle } from 'react-icons/ai';
import { FaCircleInfo } from 'react-icons/fa6';

import { Currency } from 'types/general.types';
import { TooltipIcon } from 'shared/TooltipIcon';
import { TOOLTIP_MESSAGES } from 'constants/notifications';
import { formatCurrency } from 'utils/formatters';
import { Wallet } from 'types/api/api-balance.types';
import { Theme, useTheme } from 'themes/themeProvider';

const getCurrencyIcon = (currency: Currency, theme: Theme) => {
  switch (currency) {
    case Currency.BTC:
      return <PiCurrencyBtcFill color={theme.widgets.bitcoinWallet.textColor} size={24} />;
    case Currency.EUR:
      return <AiOutlineEuro color={theme.widgets.bitcoinWallet.textColor} size={24} />;

    default:
      return <AiOutlineDollarCircle color={theme.widgets.bitcoinWallet.textColor} size={24} />;
  }
};

export const PropMap: (currency: Currency) => {
  title: { text: string; icon?: React.ReactNode };
  balance1: { text: string; subtext?: string; icon?: React.ReactNode };
  balance2: { text: string; subtext?: string; icon?: React.ReactNode };
} = currency => {
  const { theme } = useTheme();
  if (currency === Currency.BTC)
    return {
      title: { text: 'BTC Wallet', icon: getCurrencyIcon(currency, theme) },
      balance1: {
        text: 'Pending Pool Balance',
        icon: (
          <TooltipIcon
            icon={<FaCircleInfo color={theme.widgets.bitcoinWallet.textColor} />}
            tooltipId='Pending Pool Balance'
            tooltipText={TOOLTIP_MESSAGES.BALANCE}
          />
        ),
      },
      balance2: {
        text: 'Lifetime Earnings',
        icon: (
          <TooltipIcon
            icon={<FaCircleInfo color={theme.widgets.fiatWallet.textColor} />}
            tooltipId='Lifetime earnings'
            tooltipText={TOOLTIP_MESSAGES.EARNINGS}
          />
        ),
      },
    };
  return {
    title: { text: `${currency} Account`, icon: getCurrencyIcon(currency, theme) },
    balance1: {
      text: 'Active balance',
      icon: (
        <TooltipIcon
          icon={<FaCircleInfo color={theme.widgets.bitcoinWallet.textColor} />}
          tooltipId='Active balance'
          tooltipText={TOOLTIP_MESSAGES.ACTIVE_BALANCE}
        />
      ),
    },
    balance2: {
      text: 'Blocked deposit',
      icon: (
        <TooltipIcon
          icon={<FaCircleInfo color={theme.widgets.bitcoinWallet.textColor} />}
          tooltipId='Blocked deposit'
          tooltipText={TOOLTIP_MESSAGES.BLOCKED_DEPOSIT}
        />
      ),
    },
  };
};

export const cardMap: {
  [key in Currency]: (wallet: Wallet) => {
    balanceTop: number;
    balanceBottom: number;
    isSubBalance: boolean;
  };
} = {
  USD: wallet => ({
    balanceTop: wallet.balance,
    balanceBottom: wallet.deposit,
    isSubBalance: false,
  }),
  GBP: wallet => ({
    balanceTop: wallet.balance,
    balanceBottom: wallet.deposit,
    isSubBalance: false,
  }),
  EUR: wallet => ({
    balanceTop: wallet.balance,
    balanceBottom: wallet.deposit,
    isSubBalance: false,
  }),
  BTC: wallet => ({
    balanceTop: wallet.balance,
    balanceBottom: wallet.earnings,
    isSubBalance: true,
  }),
};

export const getBalance = (walletId: string, balance: number, currency: Currency) =>
  walletId ? formatCurrency(currency, balance) : '--.--';
